import React from 'react';
import { Circle, G, Line, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** FV = CFM / A_face — hood with exhaust plenum, sash, capture velocity arrows. */
export function DiagramFumehoodFaceVelocity() {
  const hx = 85, hy = 35, hw = 170, hh = 160;
  const sashY = 110;

  return (
    <DiagramCanvas>
      {/* Exhaust duct rising */}
      <Rect x={hx + hw / 2 - 14} y={10} width={28} height={hy - 10} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.4" />
      <Polygon points={`${hx + hw / 2 - 8},15 ${hx + hw / 2},9 ${hx + hw / 2 + 8},15`} fill={DT.arrow} />
      <SvgText x={hx + hw / 2 + 20} y={22} fill={DT.desc} fontSize="8" fontFamily={DT.mono}>exhaust</SvgText>

      {/* Hood body */}
      <Rect x={hx} y={hy} width={hw} height={hh} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.8" />
      {/* Sash */}
      <Rect x={hx} y={hy + 6} width={hw} height={sashY - hy - 6} fill="rgba(122,170,187,0.1)" stroke={DT.stroke} strokeWidth="0.8" />
      <Line x1={hx} y1={sashY} x2={hx + hw} y2={sashY} stroke={DT.stroke} strokeWidth="3" />
      <SvgText x={hx + hw / 2} y={hy + 60} fill={DT.desc} fontSize="8" fontFamily={DT.mono} textAnchor="middle">sash</SvgText>

      {/* Face opening */}
      <Rect x={hx} y={sashY} width={hw} height={hh - (sashY - hy) - 5} fill="rgba(255,107,26,0.06)" stroke={DT.varColor} strokeWidth="1.6" strokeDasharray="5,3" />

      {/* Capture velocity arrows entering face (uniform) */}
      {[hx + 25, hx + 60, hx + 95, hx + 130, hx + 160].map((x, i) => (
        <G key={i}>
          <Line x1={x} y1={hy + hh + 18} x2={x} y2={hy + hh - 2} stroke={DT.arrow} strokeWidth="1.4" />
          <Polygon points={`${x},${hy + hh - 2} ${x - 4},${hy + hh + 5} ${x + 4},${hy + hh + 5}`} fill={DT.arrow} />
        </G>
      ))}
      {/* Small counterflow eddy hint */}
      <Path d={`M${hx + 45},${hy + hh - 20} q4,-6 8,0 t8,0`} fill="none" stroke={DT.dim} strokeWidth="0.7" opacity={0.6} />

      {/* Rear slot suction indicators */}
      {[0, 1, 2, 3].map(i => (
        <Line key={i} x1={hx + 30 + i * 36} y1={hy + 6} x2={hx + 30 + i * 36} y2={hy + 16} stroke={DT.stroke} strokeWidth="0.8" />
      ))}

      {/* Anemometer (hot-wire) in opening */}
      <Circle cx={hx + hw / 2} cy={hy + hh - 40} r={8} fill={DT.fill} stroke={DT.varColor} strokeWidth="1.4" />
      <SvgText x={hx + hw / 2} y={hy + hh - 37} fill={DT.varColor} fontSize="6" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">FV</SvgText>
      <Line x1={hx + hw / 2} y1={hy + hh - 48} x2={hx + hw / 2} y2={hy + hh - 70} stroke={DT.stroke} strokeWidth="1" />
      <SvgText x={hx + hw / 2} y={hy + hh - 75} fill={DT.varColor} fontSize="7" fontFamily={DT.mono} textAnchor="middle">anemometer</SvgText>

      {/* CFM badge on duct */}
      <Rect x={hx + hw / 2 - 22} y={40} width={44} height={14} fill="rgba(0,0,0,0.5)" />
      <SvgText x={hx + hw / 2} y={50} fill={DT.varColor} fontSize="9" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">CFM</SvgText>

      {/* Sash height dim */}
      <Line x1={hx + hw + 14} y1={sashY} x2={hx + hw + 14} y2={hy + hh - 5} stroke={DT.varColor} strokeWidth="1" />
      <Polygon points={`${hx + hw + 14},${sashY} ${hx + hw + 10},${sashY + 6} ${hx + hw + 18},${sashY + 6}`} fill={DT.varColor} />
      <Polygon points={`${hx + hw + 14},${hy + hh - 5} ${hx + hw + 10},${hy + hh - 11} ${hx + hw + 18},${hy + hh - 11}`} fill={DT.varColor} />
      <SvgText x={hx + hw + 24} y={(sashY + hy + hh) / 2} fill={DT.varColor} fontSize="9" fontFamily={DT.mono} fontWeight="700"
        rotation="-90" originX={hx + hw + 24} originY={(sashY + hy + hh) / 2}>Sh</SvgText>

      {/* Labels */}
      <LeaderLine x1={hx + hw / 2} y1={48} x2={330} y2={30} varName="CFM" desc="exhaust volume" anchor="end" />
      <LeaderLine x1={hx + hw / 2} y1={hy + hh - 32} x2={25} y2={130} varName="FV" desc="face velocity (fpm)" anchor="start" />
      <LeaderLine x1={hx + 95} y1={hy + hh + 8} x2={25} y2={235} varName="A_face" desc="W · Sh" anchor="start" />

      <FormulaTag formula="FV = CFM / A_face    (target 80–120 fpm per ANSI Z9.5)" />
    </DiagramCanvas>
  );
}
